<script setup>
    import AgendaDetailCard from '~/components/blocks/two-six/agenda/detail-card.vue';
    import { useScheduleReasons } from '@/composables/useScheduleReasons';

    const props = defineProps({
        headline: { type: String, default: '' },
        copy: { type: String, default: '' },
        button: { type: Object, default: () => ({}) },
        sessions: { type: Array, default: () => [] },
        year: { type: Number, default: null },
    });

    // This block's list is SSR-fetched and the page is worker-cached, so it never
    // carries per-viewer reasons. Overlay them in the browser instead.
    const { load: loadReasons, reasonFor } = useScheduleReasons();
    onMounted(loadReasons);

    const uid = useId();

    const params = computed(() => {
        const p = {};
        if (props.year) p.year = props.year;
        if (props.sessions?.length) {
            // Curated list: ask the lightweight featured endpoint for ONLY these
            // sessions (by title). Previously this pulled the whole catalog off
            // /api/v1/sessions (per_page: 200) and matched client-side, which
            // serialized ~2MB of sessions into every SSR payload and OOM'd the
            // worker under load.
            p.titles = props.sessions.map((s) => s?.sessionTitle).filter(Boolean).join('\n');
        } else {
            p.limit = 12;
        }
        return p;
    });

    const { data } = useLazyAsyncData(
        `sessions-catalog-${uid}`,
        () => $fetch('/api/v1/sessions/featured', { params: params.value }),
        { watch: [params], server: true },
    );

    // Per-viewer state — on my schedule, seat held, clashing. The feed is cached
    // and shared, so it carries none of it; without this overlay the same
    // session read as conflicting on the agenda and as an ordinary card here
    // (Nick, Asana 1217708959827796).
    const { applyViewerState, needsSeat } = useViewerSessionState();

    const sessionList = computed(() => {
        const all = applyViewerState(data.value?.sessions ?? []);
        if (!props.sessions?.length) return all;
        const byTitle = new Map();
        for (const s of all) {
            if (s.title) byTitle.set(s.title.toLowerCase().trim(), s);
        }
        return props.sessions
            .map((entry) => byTitle.get((entry?.sessionTitle || '').toLowerCase().trim()))
            .filter(Boolean);
    });

    const carouselRef = ref(null);
    const canPrev = ref(false);
    const canNext = ref(false);

    let scrollEndTimer = null;

    const updateButtons = () => {
        const el = carouselRef.value;
        if (!el) return;
        const cards = el.querySelectorAll('[data-catalog-card]');
        const first = cards[0];
        const last = cards[cards.length - 1];
        if (!first || !last) {
            canPrev.value = false;
            canNext.value = false;
            return;
        }
        const cRect = el.getBoundingClientRect();
        canPrev.value = first.getBoundingClientRect().left < cRect.left - 2;
        canNext.value = last.getBoundingClientRect().right > cRect.right + 2;
    };

    // Fade only as cards scroll off the LEFT edge of the carousel.
    // Cards extending past the right viewport edge stay fully opaque.
    // During active scroll: opacity = portion still inside the viewport.
    // Once scrolling stops: snap to 0 or 1 based on a 50% threshold.
    const updateCardOpacity = (snap = false) => {
        const el = carouselRef.value;
        if (!el) return;
        const rootRect = el.getBoundingClientRect();
        el.querySelectorAll('[data-catalog-card]').forEach((card) => {
            const r = card.getBoundingClientRect();
            const offsetFromLeft = Math.max(0, rootRect.left - r.left);
            const ratio = r.width > 0 ? Math.max(0, 1 - offsetFromLeft / r.width) : 0;
            const opacity = snap ? (ratio > 0.5 ? 1 : 0) : ratio;
            card.style.opacity = `${opacity}`;
        });
    };

    const getStep = () => {
        const el = carouselRef.value;
        if (!el) return 500;
        const first = el.querySelector('[data-catalog-card]');
        if (!first) return 500;
        const width = first.getBoundingClientRect().width;
        const gap = parseFloat(getComputedStyle(el).columnGap) || 20;
        return width + gap;
    };

    const prev = () => {
        carouselRef.value?.scrollBy({ left: -getStep(), behavior: 'smooth' });
    };
    const next = () => {
        carouselRef.value?.scrollBy({ left: getStep(), behavior: 'smooth' });
    };

    const onScroll = () => {
        const el = carouselRef.value;
        el?.classList.add('is-scrolling');
        requestAnimationFrame(() => {
            updateButtons();
            updateCardOpacity(false);
        });
        clearTimeout(scrollEndTimer);
        scrollEndTimer = setTimeout(() => {
            el?.classList.remove('is-scrolling');
            updateCardOpacity(true);
        }, 120);
    };

    const onResize = () => {
        updateButtons();
        updateCardOpacity(true);
    };

    onMounted(() => {
        nextTick(() => {
            updateButtons();
            updateCardOpacity(true);
        });
        window.addEventListener('resize', onResize);
    });

    onBeforeUnmount(() => {
        window.removeEventListener('resize', onResize);
        clearTimeout(scrollEndTimer);
    });

    watch(sessionList, () => {
        nextTick(() => {
            updateButtons();
            updateCardOpacity(true);
        });
    });
</script>

<template>
    <section class="block-sessions-catalog relative bg-[var(--pink)]
        px-[var(--box-padding-lr-m)] py-[45px]
        md:px-[var(--box-padding-lr)] md:py-[90px]">

        <div class="flex flex-col gap-[14px] max-w-[785px] mb-[31px]
            md:gap-[18px] md:mb-[62px]">
            <div class="flex flex-col items-start gap-[14px]
                md:flex-row md:items-center md:gap-[40px]">
                <h2 v-if="headline"
                    class="font-hubspot-serif font-normal text-[var(--magenta)] m-0 normal-case
                        text-[calc(34/var(--base-fs)*1rem)] leading-[1.1]
                        md:text-[calc(60/var(--base-fs)*1rem)] md:whitespace-nowrap md:shrink-0"
                    v-html="headline" />
                <div v-if="button?.label && button?.url" class="hidden md:block">
                    <FragmentsPillButton :button="button" variant="magenta" />
                </div>
            </div>
            <div v-if="copy"
                class="font-hubspot-sans font-light text-[var(--magenta)]
                    text-[16px] leading-[26px] tracking-[0.32px]"
                v-html="copy" />
        </div>

        <div ref="carouselRef"
            class="catalog-carousel flex gap-[var(--box-padding-lr-m)] md:gap-[20px] overflow-x-auto snap-x snap-mandatory
                -mx-[var(--box-padding-lr-m)] px-[var(--box-padding-lr-m)]
                md:-mx-[var(--box-padding-lr)] md:px-[var(--box-padding-lr)]
                scroll-pl-[var(--box-padding-lr-m)] md:scroll-pl-[var(--box-padding-lr)]
                py-[20px] -my-[20px]"
            @scroll="onScroll">
            <div v-for="session in sessionList" :key="session.id"
                data-catalog-card
                class="shrink-0 snap-start flex w-[85vw] max-w-[480px] md:w-[480px]">
                <!-- recommendation: the AI reason the schedule builder stored for
                     this viewer. Without it a builder-added session looked no
                     different here, so removing one raised the "why are you
                     removing this?" prompt with nothing on the card explaining why
                     it was recommended (UNB). session.recommendation covers a
                     client-side fetch, which does carry the session; reasonFor
                     covers the SSR case, which does not. Empty for everyone else,
                     which renders nothing. -->
                <AgendaDetailCard
                    :session="session"
                    :year="year || 2026"
                    :enable-saved="false"
                    :recommendation="session.recommendation || reasonFor(session.unique_id)"
                    :action-required="needsSeat(session)"
                    :is-full="!!session.is_full" />
            </div>
        </div>

        <div v-if="sessionList.length > 1"
            class="mt-[30px] md:mt-[40px] flex items-center gap-[16px]">
            <FragmentsCarouselControls
                :has-prev="canPrev"
                :has-next="canNext"
                variant="magenta"
                prev-label="Previous sessions"
                next-label="Next sessions"
                @prev="prev"
                @next="next" />
            <div v-if="button?.label && button?.url" class="md:hidden">
                <FragmentsPillButton :button="button" variant="magenta" />
            </div>
        </div>
    </section>
</template>

<style>
/* Previously had `contain: paint` on the block plus `content-visibility:
   auto` on each catalog card. Those paint optimizations caused the whole
   block to skip rendering during fast page scroll, leaving only the page
   background visible until the user stopped — removed so the block stays
   painted throughout. */
.block-sessions-catalog .catalog-carousel {
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
}

.block-sessions-catalog [data-catalog-card] {
    transition: opacity 250ms ease-in-out;
}

.block-sessions-catalog .catalog-carousel.is-scrolling [data-catalog-card] {
    transition: opacity 0ms;
}

.block-sessions-catalog .catalog-carousel::-webkit-scrollbar {
    display: none;
}
</style>
